import { ProcessedCourse } from "@/common/course/types";
import { getI18nDateString } from "@/lib/date";
import {
  DEFAULT_USER_CURRENCY,
  PAYMENT_FREE,
  PAYMENT_PAID,
  PAYMENT_UPGRADABLE,
} from "@/lib/course/utils/constants";
import { type VariantColors } from "@/types/Factoid";
import getCoursePrice from "./getCoursePrice";
import { formatPrice } from "@/utils/functions/formatting";
import { getCourseStartDate, getCourseEndDate } from "./courseDates";
import getFormattedDateRange from "./getFormattedDateRange";
import isCourseArchived from "./isCourseArchived";

export type CourseInformation = {
  header: string;
  description: string;
  icon: string;
  variantColor?: VariantColors;
  tooltip?: string | null;
};

export type CourseInfoTranslate = (
  key: string,
  values?: Record<string, string | number | Date>,
) => string;

function getLevelMapped(t: CourseInfoTranslate) {
  return {
    Introductory: {
      title: t("levelIntroductory"),
      description: t("noPriorExperienceRequired"),
    },
    Intermediate: {
      title: t("levelIntermediate"),
      description: t("somePriorExperienceRequired"),
    },
    Advanced: {
      title: t("levelAdvanced"),
      description: t("extensivePriorExperienceRequired"),
    },
  };
}

export function getCourseInformation(
  course: ProcessedCourse,
  paymentOption: string | null,
  hasDateColumn: boolean | undefined,
  isArchivedExternally: boolean | undefined,
  isContentRefresh: boolean | undefined,
  t: CourseInfoTranslate,
): CourseInformation[] {
  const { minEffort, maxEffort, pacingType, weeksToComplete, isEnrollable } =
    course.activeCourseRun || {};

  const { registrationDeadline } = course.additionalMetadata?.[0] || {};

  const startingDate = getCourseStartDate(course);
  const endDate = getCourseEndDate(course);
  const endDateKey = registrationDeadline ? "enrollByDate" : "endsDate";

  const calendarInformation = isArchivedExternally
    ? {
        header: t("notCurrentlyAvailable"),
        description: t("thisCourseIsArchived"),
        icon: "event-busy",
      }
    : startingDate && endDate && hasDateColumn
      ? {
          header: t("startsDate", {
            date: getI18nDateString(new Date(startingDate), "en-US", "UTC"),
          }),
          description: t(endDateKey, {
            date: getI18nDateString(new Date(endDate), "en-US", "UTC"),
          }),
          icon: "calendar-today",
        }
      : null;

  const courseInformation = [];
  const effortInformation = weeksToComplete
    ? {
        header: t("weeksCount", { count: weeksToComplete }),
        description: getFormattedDateRange(
          minEffort,
          maxEffort,
          t("hoursPerWeek"),
        ),
        icon: "timelapse",
      }
    : null;

  const pacingInformation = {
    header: pacingType === "self_paced" ? t("selfPaced") : t("instructorPaced"),
    description:
      pacingType === "self_paced"
        ? t("progressAtYourOwnSpeed")
        : t("instructorLedOnSchedule"),
    icon: isContentRefresh ? "person" : "people",
  };

  const coursePrice =
    course.activeCourseRun && getCoursePrice(course.activeCourseRun);
  const isArchived = course.activeCourseRun
    ? isCourseArchived(course.activeCourseRun)
    : true;
  const paymentInformation =
    coursePrice && isEnrollable && !isArchived
      ? {
          header:
            paymentOption === PAYMENT_PAID ? t("costToEnroll") : t("free"),
          description:
            paymentOption === PAYMENT_UPGRADABLE
              ? t("optionalUpgradeAvailable")
              : paymentOption === PAYMENT_PAID
                ? formatPrice(coursePrice, DEFAULT_USER_CURRENCY)
                : paymentOption === PAYMENT_FREE
                  ? t("accessToCourseAtNoCost")
                  : "",
          icon: "money-outline",
        }
      : null;

  const levelMapped = getLevelMapped(t);
  const level = levelMapped[course.levelType as keyof typeof levelMapped];

  const levelInformation =
    pacingType === "instructor_paced" || !level
      ? null
      : {
          header: level.title,
          description: level.description,
          icon: "speedometer",
        };

  courseInformation.push(
    ...(isContentRefresh
      ? [levelInformation, pacingInformation, effortInformation]
      : [
          effortInformation,
          calendarInformation,
          pacingInformation,
          paymentInformation,
        ]
    ).filter(Boolean),
  );

  return courseInformation.filter(
    (course): course is CourseInformation => course !== null,
  );
}
